import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IOperationResultNoValue } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { action, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { copyToClipboard } from '../../../../models/UiUtils';
import { AccountOperationsViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { DeprecatedCloseButton } from '../../../../web/components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ConditionalAccess } from '../../ConditionalAccess';
import { AccountDetailsInfoField } from '../AccountDetailsInfoField';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IErrorMessageComponentProps, IAdminUserSessionComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	accountOperations: AccountOperationsViewModel;
}

class _ShareFileFolderLinkField extends React.Component<IProps> {
	private mLinkStringValue: string;
	private isEditing: boolean;

	constructor(props: IProps) {
		super(props);

		makeObservable<_ShareFileFolderLinkField, 'mLinkStringValue' | 'isEditing' | 'toggleEdit'>(this, {
			mLinkStringValue: observable,
			isEditing: observable,
			toggleEdit: action,
		});
	}

	public render() {
		const { className, styles, accountOperations } = this.props;
		return (
			<AccountDetailsInfoField
				label='ShareFile Folder'
				className={`sharefile-folder-link-field ${css(styleSheet.label)} ${css(...(styles || []))} ${className || ''}`}
			>
				<ConditionalAccess permission={AdminPermission.ModifyAccount} elseChildren={null}>
					{this.isEditing ? (
						<div className={css(baseStyleSheet.horizontalStack)}>
							<DeprecatedCloseButton disabled={accountOperations?.isBusy} onClick={this.toggleEdit(false, false)} />
							<TextInput
								className={css(styleSheet.input)}
								disabled={accountOperations?.isBusy}
								inputId='sharefile-folder-link-field-input'
								onChange={this.onValueChanged}
								type='text'
								value={this.mLinkStringValue || ''}
							/>
							{accountOperations?.isBusy ? (
								<LoadingSpinner type='tiny' />
							) : (
								<button className={css(baseStyleSheet.brandLink)} onClick={this.toggleEdit(false, true)}>
									<span>Save</span>
								</button>
							)}
						</div>
					) : (
						<div
							className={css(
								!accountOperations?.account?.additionalInfo?.shareFileFolderLink ? styleSheet.linkActions : null
							)}
						>
							{accountOperations?.account?.additionalInfo?.shareFileFolderLink ? (
								<>
									<div className={css(baseStyleSheet.truncateText, styleSheet.linkText)}>
										{accountOperations.account.additionalInfo.shareFileFolderLink}
									</div>
									<div className={css(baseStyleSheet.horizontalStack, styleSheet.linkActions)}>
										<button className={css(baseStyleSheet.brandLink)} onClick={this.copyLink}>
											<span>Copy Link</span>
										</button>
										<button className={css(baseStyleSheet.brandLink)} onClick={this.toggleEdit(true)}>
											<span>Edit</span>
										</button>
									</div>
								</>
							) : (
								<button className={css(baseStyleSheet.brandLink)} onClick={this.toggleEdit(true)}>
									<span>Add Link</span>
								</button>
							)}
						</div>
					)}
				</ConditionalAccess>
			</AccountDetailsInfoField>
		);
	}

	private onValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.mLinkStringValue = e.target.value;
	};

	private toggleEdit =
		(editing: boolean, save = false) =>
		() => {
			const { accountOperations } = this.props;
			let promise: Promise<any> = null;
			promise = save
				? this.save()
				: Promise.resolve(() => {
						this.mLinkStringValue = accountOperations?.account?.additionalInfo?.shareFileFolderLink;
					});
			promise.then(() => {
				this.isEditing = editing;
			});
		};

	private copyLink = () => {
		const { accountOperations } = this.props;

		if (copyToClipboard(accountOperations.account.additionalInfo.shareFileFolderLink)) {
			toast.success(`${accountOperations.account.additionalInfo.shareFileFolderLink} copied to your clipboard.`);
		}
	};

	private save = () => {
		const { accountOperations, errorMessages, logApiError } = this.props;
		return accountOperations

			.updateShareFileFolderLink(accountOperations.account, this.mLinkStringValue || null)
			?.catch((error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);

				logApiError('UpdateShareFileFolderLink-Error', error);
			});
	};
}

const ShareFileFolderLinkFieldAsObserver = observer(_ShareFileFolderLinkField);
const ShareFileFolderLinkFieldWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(ShareFileFolderLinkFieldAsObserver);
export const ShareFileFolderLinkField = withEventLogging(
	ShareFileFolderLinkFieldWithContext,
	'ShareFileFolderLinkField'
);
